import { Injectable, Injector } from '@angular/core';

import { Observable } from 'rxjs';

import { BaseApi } from '@api/base.api';
import { QueryParams, ReportApiEndPoints } from '@models/api';
import { TableData } from '@models/table';
import {
  AccountingForManagerReport,
  CommissionsByMarketerReport,
  CompanyStatementReport,
  CompletedOrdersByResearcherReport,
  JobsByCompanyReport,
  JobsByCountyReport,
  OrderIssuesByResearcherReport,
  OrderIssuesByResearchersReport,
  OrdersByResearcherReport,
  OrdersByStatusReport,
  OrdersToResearchStatusReport,
  RevenueByResearcherReport,
  SalesByMarketerReport,
  SentToClientByUserReport,
  SkippedTasksReport,
  TodayStatsReport,
  UserStatementReport
} from '@models/report';
import { ChartsData } from '@models/chart';

@Injectable({
  providedIn: 'root'
})
export class ReportApi extends BaseApi<ReportApiEndPoints> {
  constructor(injector: Injector) {
    super(injector);

    this.endPoints = this.addBaseUrl({
      GET_JOBS_BY_COMPANY: '/report/ordered',
      GET_JOBS_BY_COUNTY: '/report/county',
      GET_SALES_BY_MARKETER: '/report/marketer',
      GET_SKIPPED_TASKS: '/report/skipped',
      GET_COMPANY_STATEMENT: '/report/company',
      GET_USER_STATEMENT: '/report/customer',
      GET_USER_COMPLETE_STATEMENT: '/report/customerRepo',
      GET_ORDERS_BY_RESEARCHER: '/report/orders-by-researcher',
      GET_COMPLETED_ORDERS_BY_RESEARCHER: '/report/researcher-completion',
      GET_ORDER_ISSUES_BY_RESEARCHERS: '/order-ticket-report',
      GET_ORDER_ISSUES_BY_RESEARCHER: '/order-ticket-report/researcher/${researcherId}',
      GET_ORDERS_TO_RESEARCH_STATUS: '/report/research-status',
      GET_REVENUE_BY_RESEARCHER: '/report/researcher-revenue',
      GET_COMMISSIONS_PAID_BY_MARKETER: '/report/commissions',
      GET_COMMISSIONS_ORDERED_BY_MARKETER: '/report/commissions-ordered',
      GET_CANCELLED_ORDERS_CSV: '/report/cancellations',
      GET_SENT_TO_CLIENT_BY_USER: '/report/sent-to-client',
      GET_ACCOUNTING_FOR_MANAGER: '/report/accounting',
      GET_TODAY_STATS: '/dashboard/stats',
      GET_CHARTS_DATA: '/dashboard/charts',
      GET_ORDERS_BY_STATUS: '/dashboard/orders-by-status'
    });
  }

  public getJobsByCompany(params?: QueryParams): Observable<TableData<JobsByCompanyReport>> {
    return this.http.get<TableData<JobsByCompanyReport>>(
      this.endPoints.GET_JOBS_BY_COMPANY,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadJobsByCompany(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_JOBS_BY_COMPANY,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getJobsByCounty(params?: QueryParams): Observable<TableData<JobsByCountyReport>> {
    return this.http.get<TableData<JobsByCountyReport>>(
      this.endPoints.GET_JOBS_BY_COUNTY,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadJobsByCounty(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_JOBS_BY_COUNTY,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getSalesByMarketer(params?: QueryParams): Observable<TableData<SalesByMarketerReport>> {
    return this.http.get<TableData<SalesByMarketerReport>>(
      this.endPoints.GET_SALES_BY_MARKETER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadSalesByMarketer(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_SALES_BY_MARKETER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getSkippedTasks(params?: QueryParams): Observable<TableData<SkippedTasksReport>> {
    return this.http.get<TableData<SkippedTasksReport>>(
      this.endPoints.GET_SKIPPED_TASKS,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadSkippedTasks(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_SKIPPED_TASKS,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getCompanyStatement(params?: QueryParams): Observable<TableData<CompanyStatementReport>> {
    return this.http.get<TableData<CompanyStatementReport>>(
      this.endPoints.GET_COMPANY_STATEMENT,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadCompanyStatement(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_COMPANY_STATEMENT,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getUserStatement(params?: QueryParams): Observable<TableData<UserStatementReport>> {
    return this.http.get<TableData<UserStatementReport>>(
      this.endPoints.GET_USER_STATEMENT,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadUserStatement(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_USER_STATEMENT,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public downloadUserCompleteStatement(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_USER_COMPLETE_STATEMENT,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getOrdersByResearcher(params?: QueryParams): Observable<TableData<OrdersByResearcherReport>> {
    return this.http.get<TableData<OrdersByResearcherReport>>(
      this.endPoints.GET_ORDERS_BY_RESEARCHER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadOrdersByResearcher(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_ORDERS_BY_RESEARCHER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getCompletedOrdersByResearcher(params?: QueryParams): Observable<TableData<CompletedOrdersByResearcherReport>> {
    return this.http.get<TableData<CompletedOrdersByResearcherReport>>(
      this.endPoints.GET_COMPLETED_ORDERS_BY_RESEARCHER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadCompletedOrdersByResearcher(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_COMPLETED_ORDERS_BY_RESEARCHER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getOrderIssuesByResearchers(params?: QueryParams): Observable<TableData<OrderIssuesByResearchersReport>> {
    return this.http.get<TableData<OrderIssuesByResearchersReport>>(
      this.endPoints.GET_ORDER_ISSUES_BY_RESEARCHERS,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadOrderIssuesByResearchers(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_ORDER_ISSUES_BY_RESEARCHERS,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getOrderIssuesByResearcher(researcherId: string, params?: QueryParams): Observable<TableData<OrderIssuesByResearcherReport>> {
    const url: string = this.substituteParameters(
      this.endPoints.GET_ORDER_ISSUES_BY_RESEARCHER,
      {researcherId}
    );

    return this.http.get<TableData<OrderIssuesByResearcherReport>>(url, {params: this.setQueryParameters(params)});
  }

  public downloadOrderIssuesByResearcher(researcherId: string, responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    const url: string = this.substituteParameters(
      this.endPoints.GET_ORDER_ISSUES_BY_RESEARCHER,
      {researcherId}
    );

    return this.http.get<string | ArrayBuffer>(
      url,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getOrdersToResearchStatus(params?: QueryParams): Observable<TableData<OrdersToResearchStatusReport>> {
    return this.http.get<TableData<OrdersToResearchStatusReport>>(
      this.endPoints.GET_ORDERS_TO_RESEARCH_STATUS,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadOrdersToResearchStatus(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_ORDERS_TO_RESEARCH_STATUS,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getRevenueByResearcher(params?: QueryParams): Observable<TableData<RevenueByResearcherReport>> {
    return this.http.get<TableData<RevenueByResearcherReport>>(
      this.endPoints.GET_REVENUE_BY_RESEARCHER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadRevenueByResearcher(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_REVENUE_BY_RESEARCHER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getCommissionsPaidByMarketer(params?: QueryParams): Observable<TableData<CommissionsByMarketerReport>> {
    return this.http.get<TableData<CommissionsByMarketerReport>>(
      this.endPoints.GET_COMMISSIONS_PAID_BY_MARKETER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadCommissionsPaidByMarketer(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_COMMISSIONS_PAID_BY_MARKETER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getCommissionsOrderedByMarketer(params?: QueryParams): Observable<TableData<CommissionsByMarketerReport>> {
    return this.http.get<TableData<CommissionsByMarketerReport>>(
      this.endPoints.GET_COMMISSIONS_ORDERED_BY_MARKETER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadCommissionsOrderedByMarketer(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_COMMISSIONS_ORDERED_BY_MARKETER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getCancelledOrdersCSV(): Observable<string> {
    return this.http.get<string>(this.endPoints.GET_CANCELLED_ORDERS_CSV, {responseType: <'json'>'text'});
  }

  public getSentToClientByUser(params?: QueryParams): Observable<TableData<SentToClientByUserReport>> {
    return this.http.get<TableData<SentToClientByUserReport>>(
      this.endPoints.GET_SENT_TO_CLIENT_BY_USER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadSentToClientByUser(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_SENT_TO_CLIENT_BY_USER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getAccountingForManager(params?: QueryParams): Observable<TableData<AccountingForManagerReport>> {
    return this.http.get<TableData<AccountingForManagerReport>>(
      this.endPoints.GET_ACCOUNTING_FOR_MANAGER,
      {params: this.setQueryParameters(params)}
    );
  }

  public downloadAccountingForManager(responseType: string, params?: QueryParams): Observable<string | ArrayBuffer> {
    return this.http.get<string | ArrayBuffer>(
      this.endPoints.GET_ACCOUNTING_FOR_MANAGER,
      {responseType: <'json'>responseType, params: this.setQueryParameters(params)}
    );
  }

  public getTodayStats(params: QueryParams): Observable<TodayStatsReport> {
    return this.http.get<TodayStatsReport>(
      this.endPoints.GET_TODAY_STATS,
      {params: this.setQueryParameters(params)}
    );
  }

  public getChartsData(): Observable<ChartsData> {
    return this.http.get<ChartsData>(this.endPoints.GET_CHARTS_DATA);
  }

  public getOrdersByStatus(params?: QueryParams): Observable<TableData<OrdersByStatusReport>> {
    return this.http.get<TableData<OrdersByStatusReport>>(
      this.endPoints.GET_ORDERS_BY_STATUS,
      {params: this.setQueryParameters(params)}
    );
  }
}
